/*
* Copyright (c) 2025 Frayo and HomeChef360. All Rights Reserved.
*
* CONFIDENTIAL & PROPRIETARY
* This software and its source code are the confidential and proprietary
* information of Frayo and HomeChef360. Unauthorized copying, modification,
* distribution, disclosure, or use of this code, in whole or in part, via any
* medium, is strictly prohibited without prior written consent.
*
* Violations will be subject to legal action in the State of Florida and may
* also be pursued under applicable U.S. federal law.
*/

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import Select from "react-select";

import {
  DishCreate,
  getCuisine,
  getCategory,
  getType,
  getIngredient,
  getMeasurement,
  checkDuplicateDish,
  getDishLimit,
} from "../core/_requests";
import { useAuth } from "../../../auth";

interface Cuisine {
  uuid: string;
  name: string;
}
interface Type {
  uuid: string;
  name: string;
}
interface Category {
  uuid: string;
  name: string;
}
interface Ingredient {
  id: string;
  name: string;
}
interface Measurement {
  id: string;
  name: string;
  measurementName: string;
  dishOrderType: number;
}
interface DishAdd {
  chefId: any;
  name: string;
  categoryId: string;
  typeId: string;
  cuisineId: string;
  description: string;
  specialInstruction: string;
  ingredients: string[];
  dishType: any;
  preparationTime: string;
  preparationTimeUnit: string;
  dishImage: any;
  formImage: any;
  tags: string;
  createdBy: any;
  dishvariants: {
    unit: number;
    measurementId: string;
    price: number | "";
    discount: number | "";
    finalPrice: number | "";
  }[];
  dishAddons: {
    addonsTypeId: string;
    addonsId: string;
    createdBy: number;
  }[];
}

const AddBulkDish = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [dishes, setDishes] = useState<DishAdd[]>([
    {
      chefId: currentUser?.uuid || "",
      name: "",
      categoryId: "",
      typeId: "",
      cuisineId: "",
      description: "",
      specialInstruction: "",
      ingredients: [],
      preparationTime: "",
      preparationTimeUnit: "mins",
      dishType: 1,
      dishImage: "",
      formImage: "",
      tags: "",
      createdBy: currentUser?.chefId || 0,
      dishvariants: [
        {
          unit: 1,
          measurementId: "",
          price: "",
          discount: "",
          finalPrice: "",
        },
      ],
      dishAddons: [
        {
          addonsTypeId: "",
          addonsId: "",
          createdBy: 0,
        },
      ],
    },
  ]);

  const [cuisines, setCuisines] = useState<Cuisine[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [types, setTypes] = useState<Type[]>([]);
  const [ingredientsList, setIngredientsList] = useState<Ingredient[]>([]);
  const [measurements, setMeasurements] = useState<Measurement[]>([]);
  const [loadingDropdowns, setLoadingDropdowns] = useState(false);
  const [duplicateErrors, setDuplicateErrors] = useState<{ [key: number]: string }>({});
  const [imagePreviews, setImagePreviews] = useState<{ [key: number]: string }>({});
  const [dishLimit, setDishLimit] = useState<{ currentCount: number; maxAllowed: number }>({ currentCount: 0, maxAllowed: 0 })
  const extractArray = (res: any) => {
    if (!res) return [];
    const payload = res?.data ?? res;
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.result)) return payload.result;
    if (payload && typeof payload === "object") {
      const arr = Object.values(payload).find((v) => Array.isArray(v));
      if (Array.isArray(arr)) return arr;
    }
    return [];
  };

  const parsePreparationTime = (timeString: string) => {
    if (!timeString) return { value: "", unit: "mins" };
    const parts = timeString.split(" ");
    return {
      value: parts[0] || "",
      unit: parts[1] || "mins"
    };
  };

 useEffect(() => {
  const checkDuplicates = async () => {
    const newErrors: { [key: number]: string } = {};

    dishes.forEach((dish, index) => {
      if (!dish.name.trim()) return;

      // ✔ Check duplicate in current bulk list
      const duplicateIndex = dishes.findIndex(
        (d, i) =>
          i !== index &&
          d.name.trim().toLowerCase() === dish.name.trim().toLowerCase()
      );

      if (duplicateIndex !== -1) {
        newErrors[index] = "Dish name already entered ";
      }
    });

   
    for (let i = 0; i < dishes.length; i++) {
      const dish = dishes[i];
      if (dish.name.trim() && !newErrors[i]) {
        try {
          const response = await checkDuplicateDish(dish.chefId, dish.name);
          if (response?.isDuplicate) {
            newErrors[i] = "Dish name already exists in your list";
          }
        } catch (err) {
          console.error("Error checking duplicate dish:", err);
        }
      }
    }

    setDuplicateErrors(newErrors);
  };

  const timer = setTimeout(() => {
    checkDuplicates();
  }, 300);

  return () => clearTimeout(timer);
}, [dishes]);





  useEffect(() => {
    const fetchDropdownData = async () => {
      setLoadingDropdowns(true);
      try {
        const [cuisineRes, categoryRes, typeRes, ingredientRes, measurementRes] =
          await Promise.all([
            getCuisine(),
            getCategory(currentUser?.uuid),
            getType(),
            getIngredient(currentUser?.uuid),
            getMeasurement(),
          ]);

        setCuisines(extractArray(cuisineRes));
        setCategories(extractArray(categoryRes));
        setTypes(extractArray(typeRes));
        setIngredientsList(extractArray(ingredientRes));
        setMeasurements(extractArray(measurementRes));
      } catch (err) {
        console.error("Error fetching dropdowns", err);
        toast.error("Failed to load dropdown data.");
      } finally {
        setLoadingDropdowns(false);
      }
    };

    fetchDropdownData();
  }, [currentUser?.uuid]);
  useEffect(() => {
    const fetchDishLimit = async () => {
      try {
        if (!currentUser?.uuid) return;
        const res = await getDishLimit(currentUser.uuid);
        setDishLimit(res.data);
      } catch (err) {
        console.error("Error fetching dish limit:", err);
        toast.error("Failed to fetch dish limit");
      }
    };
    fetchDishLimit();
  }, [currentUser?.uuid]);
  const addNewDish = () => {
    if (dishLimit.currentCount + dishes.length >= dishLimit.maxAllowed) {
      toast.error(`You can only add up to ${dishLimit.maxAllowed} dishes as per your plan.`);
      return;
    }
    setDishes([
      ...dishes,
      {
        ...dishes[0],
        name: "",
        description: "",
        specialInstruction: "",
        ingredients: [],
        preparationTime: "",
        preparationTimeUnit: "mins",
        typeId: "",
        cuisineId: "",
        categoryId: "",
        dishImage: "",
        dishvariants: [
          {
            unit: 1,
            measurementId: "",
            price: "",
            discount: "",
            finalPrice: "",
          },
        ],
        dishAddons: [],
      },
    ]);
  };
const handleIngredientSelect = (dishIndex: number, values: string[]) => {
  const updated = [...dishes];
  updated[dishIndex].ingredients = values;
  setDishes(updated);
};

  const removeDish = (index: number) => {
    const updated = [...dishes];
    updated.splice(index, 1);
    setDishes(updated);
    setDuplicateErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[index];
      return newErrors;
    });
    setImagePreviews(prev => {
      const newPreviews = { ...prev };
      delete newPreviews[index];
      return newPreviews;
    });
  };

  const handleFieldChange = (
    index: number,
    field: keyof DishAdd,
    value: any
  ) => {
    const updated = [...dishes];
    updated[index] = {
      ...updated[index],
      [field]: value,
    };
    setDishes(updated);

    if (field === "name" && duplicateErrors[index]) {
      setDuplicateErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[index];
        return newErrors;
      });
    }
  };

  const handleImageChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }

    const updated = [...dishes];
    updated[index].dishImage = file;
    setDishes(updated);

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreviews(prev => ({
        ...prev,
        [index]: e.target?.result as string
      }));
    };
    reader.readAsDataURL(file);
  };
  // Capitalize each word in a string
  const capitalizeWords = (str: string) => {
    return str
      .split(" ")
      .filter(Boolean)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };
  const handleAddIngredient = (index: number, value: string) => {
    if (!value) return;
    const updated = [...dishes];
    if (!updated[index].ingredients.includes(value)) {
      updated[index].ingredients.push(value);
    }
    setDishes(updated);
  };


  const handleVariantChange = (
    dishIndex: number,
    variantIndex: number,
    field: keyof DishAdd["dishvariants"][0],
    value: any
  ) => {
    const updated = [...dishes];
    const variant = updated[dishIndex].dishvariants[variantIndex];
    if (!variant) return;

    if (field === "price" || field === "discount") {
      const price = field === "price" ? (value === "" ? "" : Number(value)) : variant.price;
      const discount = field === "discount" ? (value === "" ? "" : Number(value)) : variant.discount;

      let finalPrice: number | "" = "";
      if (price !== "" && discount !== "") {
        finalPrice = Math.max(0, Number(price) - Number(discount));
      } else if (price !== "") {
        finalPrice = Number(price);
      }

      updated[dishIndex].dishvariants[variantIndex] = {
        ...variant,
        [field]: value,
        price,
        discount,
        finalPrice,
      };
    } else {
      updated[dishIndex].dishvariants[variantIndex] = {
        ...variant,
        [field]: value,
      };
    }
    setDishes(updated);
  };

  const handlePreparationTimeChange = (index: number, value: string) => {
    const updated = [...dishes];
    const currentTime = parsePreparationTime(updated[index].preparationTime);
    updated[index].preparationTime = `${value} ${currentTime.unit}`;
    setDishes(updated);
  };

  const handlePreparationTimeUnitChange = (index: number, value: string) => {
    const updated = [...dishes];
    const currentTime = parsePreparationTime(updated[index].preparationTime);
    updated[index].preparationTime = `${currentTime.value} ${value}`;
    updated[index].preparationTimeUnit = value;
    setDishes(updated);
  };

  const convertToFormData = (dish: DishAdd): FormData => {
    const formData = new FormData();
    const capitalizedName = capitalizeWords(dish.name);
    formData.append("chefId", dish.chefId);
    formData.append("name", capitalizedName)
    formData.append("categoryId", dish.categoryId);
    formData.append("typeId", dish.typeId);
    formData.append("cuisineId", dish.cuisineId);
    formData.append("description", dish.description || "");
    formData.append("specialInstruction", dish.specialInstruction || "");
   formData.append("ingredients", dish.ingredients.join(","));



    formData.append("preparationTime", dish.preparationTime);
    formData.append("dishType", dish.dishType.toString());
    formData.append("tags", dish.tags);
    formData.append("createdBy", dish.createdBy.toString());

    if (dish.dishImage instanceof File) formData.append("dishImage", dish.dishImage);
    if (dish.formImage instanceof File) formData.append("formImage", dish.formImage);

    dish.dishvariants.forEach((variant, i) => {
      formData.append(`dishvariants[${i}][unit]`, variant.unit.toString());
      formData.append(`dishvariants[${i}][measurementId]`, variant.measurementId.toString());
      formData.append(`dishvariants[${i}][price]`, (variant.price || 0).toString());
      formData.append(`dishvariants[${i}][discount]`, (variant.discount || 0).toString());
      formData.append(`dishvariants[${i}][finalPrice]`, (variant.finalPrice || 0).toString());
    });

    dish.dishAddons.forEach((addon, i) => {
      formData.append(`dishAddons[${i}][addonsTypeId]`, addon.addonsTypeId);
      formData.append(`dishAddons[${i}][addonsId]`, addon.addonsId);
      formData.append(`dishAddons[${i}][createdBy]`, addon.createdBy.toString());
    });

    return formData;
  };

  const validateDish = (dish: DishAdd, index: number): string[] => {
    const errors: string[] = [];

    if (!dish.name.trim()) errors.push("Dish name is required");
    if (!dish.categoryId) errors.push("Category is required");
    if (!dish.typeId) errors.push("Type is required");
    if (!dish.cuisineId) errors.push("Cuisine is required");
    if (!dish.ingredients || dish.ingredients.length === 0)
      errors.push("At least one ingredient is required");

    if (!dish.preparationTime) errors.push("Preparation time is required");
    if (!dish.dishvariants[0]?.measurementId) errors.push("Measurement is required");
    if (dish.dishvariants[0]?.price === "") errors.push("Price is required");
    if (dish.dishvariants[0]?.price !== "" && Number(dish.dishvariants[0]?.price) <= 0) errors.push("Price must be greater than 0");
    if (dish.dishvariants[0]?.discount !== "" && Number(dish.dishvariants[0]?.discount) < 0) errors.push("Discount cannot be negative");
    if (dish.dishvariants[0]?.finalPrice !== "" && Number(dish.dishvariants[0]?.finalPrice) <= 0) errors.push("Final price must be greater than 0");


    return errors;
  };

  const submitAll = async () => {
    if (Object.keys(duplicateErrors).length > 0) {
      toast.error("Please fix duplicate dish names before saving");
      return;
    }

    const allErrors: { [key: number]: string[] } = {};
    let hasErrors = false;

    // Validate all dishes
    dishes.forEach((dish, index) => {
      const errors = validateDish(dish, index);
      if (errors.length > 0) {
        allErrors[index] = errors;
        hasErrors = true;
      }
    });

    if (hasErrors) {
      // Show first error found
      const firstErrorIndex = Object.keys(allErrors)[0];
      toast.error(`Dish ${Number(firstErrorIndex) + 1}: ${allErrors[Number(firstErrorIndex)][0]}`);
      return;
    }

    try {
      const results = await Promise.all(
        dishes.map((d) => DishCreate(convertToFormData(d)))
      );
      if (results.every((res) => res?.status === true)) {
        toast.success("All dishes saved successfully!");
        setTimeout(() => navigate("/dish/success"), 1500);
      } else {
        toast.error("Some dishes failed to save. Please check your data.");
        console.warn("Some items failed", results);
      }
    } catch (err) {
      console.error("Error saving dishes:", err);
      toast.error("An error occurred while saving dishes.");
    }
  };

  return (
    <>
      <ToastContainer />
      <section className="container-fluid">
        <div className="row">
          <section className="content-section">
            <section className="row main-content">
              <div className="container-fluid bulk-dish-container">
                <div className="card">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <h3 className="mb-0">Bulk Dish Creation</h3>
                    <div>
                      <button
                        type="button"
                        className="btn btn-primary me-2"
                        onClick={addNewDish}
                      >
                        Add Dish
                      </button>
                      <button
                        type="button"
                        className="btn btn-success"
                        onClick={submitAll}
                      >
                        Save All
                      </button>
                    </div>
                  </div>

                  <div className="card-body">
                    <div
                      className="table-responsive"
                      style={{ overflowX: "auto", width: "100%" }}
                    >
                      <table className="table table-bordered table-hover align-middle" style={{ minWidth: "1800px" }}>
                        <thead className="table-light">
                          <tr>
                            <th style={{ width: "50px" }}>#</th>
                            <th style={{ minWidth: "150px" }}>Dish Name</th>
                            <th style={{ minWidth: "120px" }}>Cuisine</th>
                            <th style={{ minWidth: "120px" }}>Category</th>
                            <th style={{ minWidth: "120px" }}>Type</th>
                            <th style={{ minWidth: "150px" }}>Ingredients</th>
                            <th style={{ minWidth: "120px" }}>Measurement</th>
                            <th style={{ minWidth: "100px" }}>Unit/Count</th>
                            <th style={{ minWidth: "100px" }}>Price ($)</th>
                            <th style={{ minWidth: "100px" }}>Discount</th>
                            <th style={{ minWidth: "100px" }}>Final Price ($)</th>
                            <th style={{ minWidth: "120px" }}>Preparation Time</th>
                            <th style={{ minWidth: "120px" }}>Preparation Unit</th>
                            <th style={{ minWidth: "200px" }}>Description (Optional)</th>
                            <th style={{ minWidth: "200px" }}>Special Instruction (Optional)</th>
                            <th style={{ minWidth: "150px" }}>Dish Image</th>
                            <th style={{ width: "100px" }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dishes.map((dish, dishIndex) => {
                            const prepTime = parsePreparationTime(dish.preparationTime);
                            return (
                              <tr key={dishIndex}>
                                <td>{dishIndex + 1}</td>
                                <td>
                                  <div className="position-relative">
                                    <input
                                      type="text"
                                      className={`form-control form-control-sm ${duplicateErrors[dishIndex] ? "is-invalid" : ""
                                        }`}
                                      value={dish.name}
                                      onChange={(e) =>
                                        handleFieldChange(dishIndex, "name", e.target.value)
                                      }
                                      placeholder="Enter dish name"
                                    />
                                    {duplicateErrors[dishIndex] && (
                                      <div className="invalid-feedback" style={{ position: 'absolute', whiteSpace: 'nowrap' }}>
                                        {duplicateErrors[dishIndex]}
                                      </div>
                                    )}
                                  </div>
                                </td>
                                <td>
                                  <select
                                    className="form-select form-select-sm"
                                    value={dish.cuisineId}
                                    onChange={(e) =>
                                      handleFieldChange(dishIndex, "cuisineId", e.target.value)
                                    }
                                  >
                                    <option value="">Select Cuisine</option>
                                    {cuisines.map((cui) => (
                                      <option key={cui.uuid} value={cui.uuid}>
                                        {cui.name}
                                      </option>
                                    ))}
                                  </select>
                                </td>
                                <td>
                                  <select
                                    className="form-select form-select-sm"
                                    value={dish.categoryId}
                                    onChange={(e) =>
                                      handleFieldChange(dishIndex, "categoryId", e.target.value)
                                    }
                                  >
                                    <option value="">Select Category</option>
                                    {categories.map((cat) => (
                                      <option key={cat.uuid} value={cat.uuid}>
                                        {cat.name}
                                      </option>
                                    ))}
                                  </select>
                                </td>
                                <td>
                                  <select
                                    className="form-select form-select-sm"
                                    value={dish.typeId}
                                    onChange={(e) =>
                                      handleFieldChange(dishIndex, "typeId", e.target.value)
                                    }
                                  >
                                    <option value="">Select Type</option>
                                    {types.map((t) => (
                                      <option key={t.uuid} value={t.uuid}>
                                        {t.name}
                                      </option>
                                    ))}
                                  </select>
                                </td>
                               <td>
  <div className="select-wrapper" style={{ minWidth: "150px" }}>
    <select
      className="rounded-select"
      value={
        dish.ingredients.length > 0 
          ? dish.ingredients[dish.ingredients.length - 1] 
          : ""
      }
      onChange={(e) => handleAddIngredient(dishIndex, e.target.value)}
    >
      <option value="">Select multiple ingredients</option>
      {ingredientsList.map((ing) => (
        <option key={ing.id} value={ing.id}>
          {ing.name}
        </option>
      ))}
    </select>
  </div>

  <div className="d-flex flex-wrap gap-1 mt-1">
    {dish.ingredients.map((id) => {
      const ingredient = ingredientsList.find((i) => i.id === id);
      if (!ingredient) return null;

      return (
        <span
          key={id}
          className="ingredient-chip"
          onClick={() => {
            const updated = [...dishes];
            updated[dishIndex].ingredients =
              updated[dishIndex].ingredients.filter((ing) => ing !== id);
            setDishes(updated);
          }}
        >
          {ingredient.name} ✕
        </span>
      );
    })}
  </div>
</td>


                                <td>
                                  <select
                                    className="form-select form-select-sm"
                                    value={dish.dishvariants[0]?.measurementId || ""}
                                    onChange={(e) =>
                                      handleVariantChange(
                                        dishIndex,
                                        0,
                                        "measurementId",
                                        e.target.value
                                      )
                                    }
                                  >
                                    <option value="">Select Measurement</option>
                                    {measurements.length > 0 ? (
                                      measurements.map((m) => (
                                        <option key={m.id} value={m.id}>
                                          {m.measurementName}
                                        </option>
                                      ))
                                    ) : (
                                      <option disabled>Loading measurements...</option>
                                    )}
                                  </select>
                                </td>
                                <td>
                                  <input
                                    type="number"
                                    min={1}
                                    className="form-control form-control-sm"
                                    value={dish.dishvariants[0]?.unit}
                                    onChange={(e) =>
                                      handleVariantChange(dishIndex, 0, "unit", e.target.value)
                                    }

                                  />
                                </td>
                                <td>
                                  <input
                                    type="number"
                                    min={0}
                                    step={0.01}
                                    className="form-control form-control-sm"
                                    value={dish.dishvariants[0]?.price || ""}
                                    onChange={(e) =>
                                      handleVariantChange(
                                        dishIndex,
                                        0,
                                        "price",
                                        e.target.value
                                      )
                                    }
                                    placeholder="0.00"
                                  />
                                </td>
                                <td>
                                  <input
                                    type="number"
                                    min={0}
                                    step={0.01}
                                    className="form-control form-control-sm"
                                    value={dish.dishvariants[0]?.discount || ""}
                                    onChange={(e) =>
                                      handleVariantChange(
                                        dishIndex,
                                        0,
                                        "discount",
                                        e.target.value
                                      )
                                    }
                                    placeholder="0.00"
                                  />
                                </td>
                                <td>
                                  <input
                                    type="number"
                                    className="form-control form-control-sm"
                                    value={(dish.dishvariants[0]?.finalPrice || "").toString()}
                                    readOnly
                                    disabled
                                    placeholder="0.00"
                                  />
                                </td>
                                <td>
                                  <input
                                    type="number"
                                    min={0}
                                    className="form-control form-control-sm"
                                    value={prepTime.value}
                                    onChange={(e) =>
                                      handlePreparationTimeChange(dishIndex, e.target.value)
                                    }
                                    placeholder="Time value"
                                  />
                                </td>
                                <td>
                                  <select
                                    className="form-select form-select-sm"
                                    value={dish.preparationTimeUnit}
                                    onChange={(e) =>
                                      handlePreparationTimeUnitChange(dishIndex, e.target.value)
                                    }
                                  >
                                    <option value="mins">Mins</option>
                                    <option value="hrs">Hrs</option>
                                  </select>
                                </td>
                                <td>
                                  <textarea
                                    className="form-control form-control-sm"
                                    value={dish.description}
                                    onChange={(e) =>
                                      handleFieldChange(dishIndex, "description", e.target.value)
                                    }
                                    placeholder="Optional description"
                                    rows={2}
                                  />
                                </td>
                                <td>
                                  <textarea
                                    className="form-control form-control-sm"
                                    value={dish.specialInstruction}
                                    onChange={(e) =>
                                      handleFieldChange(
                                        dishIndex,
                                        "specialInstruction",
                                        e.target.value
                                      )
                                    }
                                    placeholder="Optional special instructions"
                                    rows={2}
                                  />
                                </td>
                                <td>
                                  <div className="d-flex flex-column">
                                    <input
                                      type="file"
                                      className="form-control form-control-sm mb-2"
                                      accept="image/*"
                                      onChange={(e) => handleImageChange(dishIndex, e)}
                                    />
                                    {imagePreviews[dishIndex] && (
                                      <img
                                        src={imagePreviews[dishIndex]}
                                        alt="Preview"
                                        style={{ width: "50px", height: "50px", objectFit: "cover" }}
                                        className="rounded"
                                      />
                                    )}
                                  </div>
                                </td>
                                <td>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-danger"
                                    onClick={() => removeDish(dishIndex)}
                                  >
                                    Remove
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      {loadingDropdowns && <div>Loading dropdowns...</div>}
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </section>
        </div>
      </section>
    </>
  );
};

export default AddBulkDish;  